import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Nav } from '../components/Nav';
import { TableOfContents } from '../components/TableOfContents';
import { CardDisplay } from '../components/CardDisplay';
import { TrickExample } from '../components/TrickExample';
import { RuleCallout } from '../components/RuleCallout';
import { BackToTop } from '../components/BackToTop';
import type { SectionInfo } from '../types/rules';
import {
  FileText,
  MessageSquare,
  Layers,
  Users,
  Settings,
  Archive,
  Zap,
  TrendingUp,
  UserPlus,
  Home,
  ChevronRight,
  Copy,
  Check,
} from 'lucide-react';

// Section mapping for ToC
const rulesSections: ReadonlyArray<SectionInfo> = [
  { id: 'quick-reference', title: 'Quick Reference' },
  { id: 'terminology', title: 'Terminology' },
  { id: 'deck-setup', title: 'Deck Setup' },
  { id: 'teams-levels', title: 'Teams & Levels' },
  { id: 'round-setup', title: 'Round Setup' },
  { id: 'bottom-cards', title: 'Bottom Cards' },
  { id: 'trick-play', title: 'Trick Play' },
  { id: 'scoring', title: 'Scoring' },
  { id: 'finding-friends', title: 'Finding Friends' },
];

// Markdown content for copying to ChatGPT
const RULES_MARKDOWN = `# Shengji Rules

The complete rules for Shengji (升级), a classic Chinese trick-taking card game.

## Quick Reference

| Item | Standard Value |
|------|----------------|
| Players | 4 (fixed partnerships); Finding Friends supports 5–7 with fluid teams |
| Decks | 1 deck per two players (54 cards each: 13 ranks × 4 suits + 2 jokers) |
| Point Cards | 5 = 5 pts, 10 = 10 pts, K = 10 pts (200 pts total per two decks) |
| Goal | Declarers defend 0–75 opponent points to level up; opponents capture ≥80 to take over |
| Win Condition | First team to exceed Ace (e.g., reach 2) wins the match |

## Terminology

- **Declarers** (庄家方): Team that won the bid. They set the trump suit and must limit opponent points.
- **Opponents** (防守方): Team defending against the declarers. They try to capture enough points to take over.
- **Zhuangjia** (庄家): Leader of the declarer team (the one who won the bid). They get the bottom cards and lead first.
- **Trump Rank** (级牌): A rank determined by the current level (e.g., Level 2 means all 2s are special).
- **Trump Suit** (主花色): A suit chosen by the zhuangjia. All cards of this suit plus the trump rank cards and jokers are trumps.
- **Bottom** (底牌): 8 cards dealt face-down that the zhuangjia examines and replaces with discards.
- **Level** (级): The current rank being contested (starts at 2, progresses upward through the ranks).
- **Point Cards** (分): 5s, 10s, and Ks. Each 5 = 5 points, 10/K = 10 points. Declarers defend 0–75, opponents need 80+.

## Deck Setup

### Card Distribution (4 players, 2 decks)

- **Total cards**: 108 cards
- **Per player**: 25 cards each (100 total)
- **Bottom**: 8 cards (face-down)

### Card Ranking Within Suits

**Ascending order** (low to high):
2 < 3 < 4 < 5 < 6 < 7 < 8 < 9 < 10 < J < Q < K < A

### Special Cases

- **Trump rank**: When Level 2, all 2s become special. They rank ABOVE Ace in their natural suit.
- **Jokers**: Small joker < Big joker. Jokers are the highest cards in the trump suit.

### Trump Suit Hierarchy

When the trump suit is determined (e.g., Spades + Level 2):

1. Small joker (lowest trump)
2. Big joker
3. Trump rank in non-trump suits (2♥, 2♦, 2♣)
4. Trump rank in trump suit (2♠)
5. Natural trump suit cards ascending (3♠, 4♠, ..., A♠)

## Teams & Levels

### Team Formation

- **Fixed partnerships**: Players opposite each other (e.g., North & South vs East & West).
- **Finding Friends**: Teams determined dynamically by friend cards (see Finding Friends section).

### Level Progression

- Starting level: **2** (or agreed-upon rank)
- After each round, the winner's level increases by the number of levels gained.
- Progression: 2 → 3 → 4 → 5 → 6 → 7 → 8 → 9 → 10 → J → Q → K → A → **2 (wins)**

### Determining the Declarers

Each round starts with a **bidding phase**:
1. Players bid the number of levels they think their team can win (minimum: 1 level).
2. Highest bidder wins and becomes **zhuangjia** (leader).
3. The zhuangjia's team becomes the **declarers** for the round.

## Round Setup

1. **Deal**: Each player receives 25 cards; 8 cards go to the bottom.
2. **Bid**: Players bid levels. Highest bidder becomes zhuangjia.
3. **Zhuangjia picks trump suit**: Based on their hand strength.
4. **Zhuangjia takes bottom**: They examine the 8 face-down cards.
5. **Discard & replace**: Zhuangjia discards 8 cards from their combined hand and keeps the bottom cards.
6. **Friend declaration** (Finding Friends only): Zhuangjia declares friend cards.
7. **Play**: Zhuangjia leads the first trick.

## Bottom Cards

The bottom contains 8 cards dealt face-down at the start of each round.

### Leader Privileges

- The **zhuangjia (leader)** adds the bottom cards to their hand
- They discard exactly 8 cards face-down to replace the bottom
- The new bottom stays face-down during play

### Scoring Impact

At round end:
- If declarers win (≤75 opponent points): Bottom points are **deducted** from opponent total
- If opponents win (≥80 points): They **capture** the bottom points

Example: Opponents capture 80 points in tricks + 35 in bottom = 115 total.

## Trick Play

### Trick Formats

| Format | Description | Example |
|--------|-------------|---------|
| Single | One card | A♠ |
| Pair | Two identical ranks | K♠K♠ |
| Tractor | Two+ consecutive pairs | 9♠9♠10♠10♠ |
| Throw | Single + pair + tractor combo | A♠ + K♠K♠ + Q♠J♠ |

**Critical rule**: All cards in a throw must be from the **same suit**.

### Following Rules

- **Must follow suit** if possible
- Must play the **same format** (single, pair, tractor) if possible
- If unable to follow, may play any card(s)
- Trump can be played at any time (even when able to follow)

### Winning Tricks

- Highest trump card wins
- If no trump, highest card of the **led suit** wins
- Winner of each trick leads the next

### Throw Example

Player A leads: A♠ + K♠K♠ + Q♠J♠ (a throw)

Player B must follow:
- A single spade (or trump if unable)
- A pair of spades (or trump if unable)
- A spade tractor (or trump if unable)

If Player B holds the A♠, K♠K♠, and Q♠J♠, they would win all components.

## Scoring

### Point Cards

- **5** = 5 points
- **10** = 10 points
- **K** = 10 points
- Total per 2 decks: **200 points**

### Level Changes (2-Deck Standard)

| Opponents' Points | Outcome |
|--------------------|---------|
| 0 | Declarers +3 levels |
| 5–75 | Declarers +2 levels |
| 80–155 | Declarers +1 level |
| 160–185 | Opponents become declarers, level unchanged |
| 190–215 | Opponents +1 level |
| 220–245 | Opponents +2 levels |
| ≥250 | Opponents +3 levels (+1 extra per additional 40 points) |

### Must-Stop Levels

When the declarers gain enough levels to surpass Ace, the match ends immediately in their favor. Some playgroups enforce "must-stop" levels (e.g., cannot skip rank 10 or Ace); in that case levels cap at the mandatory rank even if the table indicates a larger jump.

## Finding Friends

After inspecting the bottom, the **zhuangjia** specifies which cards recruit teammates. A friend declaration ties a specific card to an ordinal appearance (e.g., "second A♠ played").

### Constraints

- Friend cards must be ≤ Ace; trump-rank cards and jokers may not be declared.
- The leader may inadvertently declare their own cards; if they play the declared card in the specified position, they remain solo until another friend card is revealed.
- Once a friend card is revealed in a trick, the player immediately joins the defending team, and any points they have already won transfer to that team.

### How It Works

1. **Declaration**: Zhuangjia announces friend cards before play starts
2. **Revelation**: When a declared card is played, its holder reveals themselves
3. **Team change**: The friend switches to the declarer team (if not already)

### Example

5 players, Zhuangjia declares:
- First A♠ played = friend
- Second 10♥ played = friend

When the first A♠ appears, that player joins the declarers.

### No Friend Scenario

If declared cards never appear (e.g., "first K♠" but no K♠ is played), declarers play alone.

### Scoring with Friends

- Points earned by friends count for the declarer team
- Friends contribute to level-ups just like original declarers
- Team composition can change mid-round as friends are revealed
`;

export function RulesPage() {
  const [copied, setCopied] = useState(false);

  const handleCopyToClipboard = async () => {
    try {
      // eslint-disable-next-line no-undef
      await navigator.clipboard.writeText(RULES_MARKDOWN);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Nav />
      <TableOfContents sections={rulesSections} />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-black mb-4">
              Shengji Rules
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              The complete rules for Shengji (升级), a classic Chinese
              trick-taking card game.
            </p>
          </div>
        </section>

        {/* Breadcrumb */}
        <nav
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4"
          aria-label="Breadcrumb"
        >
          <div className="flex items-center justify-between">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link
                  to="/"
                  className="flex items-center gap-1 text-gray-600 hover:text-black"
                >
                  <Home className="w-4 h-4" />
                  Home
                </Link>
              </li>
              <li>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </li>
              <li className="text-black font-medium">Rules</li>
            </ol>
            <div className="group relative flex items-center">
              <button
                onClick={handleCopyToClipboard}
                className={`text-sm transition-colors ${
                  copied ? 'text-green-600' : 'text-gray-600 hover:text-black'
                }`}
              >
                {copied ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
              <div className="absolute top-full mt-2 right-0 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200">
                {copied ? 'Copied!' : 'Copy to ChatGPT'}
                <div className="absolute top-0 right-3 -translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
              </div>
            </div>
          </div>
        </nav>

        {/* Quick Reference Section */}
        <section id="quick-reference" className="py-12 scroll-mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-8 h-8 text-red" />
              <h2 className="text-3xl font-bold text-black">Quick Reference</h2>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6 overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-3 px-4 font-semibold text-black">Item</th>
                    <th className="py-3 px-4 font-semibold text-black">
                      Standard Value
                    </th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                    <td className="py-3 px-4 font-medium">Players</td>
                    <td className="py-3 px-4">
                      4 (fixed partnerships); Finding Friends supports 5–7 with
                      fluid teams
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                    <td className="py-3 px-4 font-medium">Decks</td>
                    <td className="py-3 px-4">
                      1 deck per two players (54 cards each: 13 ranks × 4 suits
                      + 2 jokers)
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                    <td className="py-3 px-4 font-medium">Point Cards</td>
                    <td className="py-3 px-4">
                      5 = 5 pts, 10 = 10 pts, K = 10 pts (200 pts total per two
                      decks)
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                    <td className="py-3 px-4 font-medium">Goal</td>
                    <td className="py-3 px-4">
                      Declarers defend 0–75 opponent points to level up;
                      opponents capture ≥80 to take over
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium">Trump Definition</td>
                    <td className="py-3 px-4">
                      Trump rank = current declarer level; trump suit = winning
                      bid suit or &quot;no-trump&quot; when jokers win
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Terminology Section */}
        <section id="terminology" className="py-12 scroll-mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <MessageSquare className="w-8 h-8 text-red" />
              <h2 className="text-3xl font-bold text-black">Terminology</h2>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <ul className="space-y-4 text-gray-700">
                <li>
                  <strong className="text-black">Hierarchy:</strong> Card
                  grouping type (single, pair, triple, tractors, throws).
                </li>
                <li>
                  <strong className="text-black">Rank:</strong> Card value for
                  ordering. The declarer rank is promoted above Ace for that
                  round.
                </li>
                <li>
                  <strong className="text-black">Trump Rank / Suit:</strong>{' '}
                  Rank equal to the declarer level and the suit won in bidding.
                  Cards of the trump rank belong to the trump suit regardless of
                  printed suit.
                </li>
                <li>
                  <strong className="text-black">Zhuangjia (当庄):</strong>{' '}
                  Leader of the declarer team; takes the bottom cards and leads
                  the first trick.
                </li>
                <li>
                  <strong className="text-black">Bottom Cards (底牌):</strong>{' '}
                  Reserve pile revealed only when the opponents win the final
                  trick.
                </li>
                <li>
                  <strong className="text-black">Shuai:</strong> Any composite
                  lead that is not a single hierarchy (e.g., combo of singles,
                  pairs, tractors).
                </li>
                <li>
                  <strong className="text-black">Length:</strong> Number of
                  connected ranks in a tractor sequence.
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Deck Setup Section */}
        <section id="deck-setup" className="py-12 scroll-mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <Layers className="w-8 h-8 text-red" />
              <h2 className="text-3xl font-bold text-black">Deck Setup</h2>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <ol className="space-y-6 text-gray-700">
                <li>
                  <strong className="text-black">Deck Count:</strong> Use one
                  54-card deck per two players (rounded down).
                  <ul className="ml-6 mt-2 space-y-1">
                    <li>• 4–5 players → 2 decks (108 cards, 4 jokers)</li>
                    <li>• 6 players → 3 decks (162 cards, 6 jokers)</li>
                  </ul>
                </li>
                <li>
                  <strong className="text-black">
                    Card Order Within Trump Suit (highest → lowest):
                  </strong>
                  <ol className="ml-6 mt-2 space-y-1 list-decimal">
                    <li>Colored jokers (big)</li>
                    <li>Uncolored jokers (small)</li>
                    <li>Cards that match both trump rank and trump suit</li>
                    <li>
                      Remaining trump-rank cards (equal strength among
                      themselves)
                    </li>
                    <li>
                      Other cards in the trump suit (A down to 2, skipping trump
                      rank)
                    </li>
                  </ol>
                </li>
                <li>
                  <strong className="text-black">Non-Trump Suits:</strong> Ace
                  down to 2 with the trump rank removed. When no trump suit is
                  declared (&quot;no-trump&quot;), jokers plus every card of the
                  trump rank form a fifth suit ordered: big jokers → small
                  jokers → trump-rank cards (all equal).
                </li>
                <li>
                  <strong className="text-black">Point Cards:</strong> Kings and
                  tens are worth 10 each; fives are worth 5. All other cards are
                  zero-point blockers. Two decks yield 200 total points; each
                  additional deck adds another 100 points.
                </li>
              </ol>

              <div className="mt-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-lg font-semibold text-black mb-4">
                  Visual Example: Hearts Trump, Rank 7
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  When hearts are trump and rank 7 is the trump rank, the card
                  hierarchy from highest to lowest is:
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-medium text-gray-600 w-32">
                      Level 1 (Highest):
                    </span>
                    <CardDisplay suit="joker" rank="Big" isColoredJoker />
                    <CardDisplay suit="joker" rank="Big" isColoredJoker />
                    <span className="text-xs text-gray-500 ml-2">
                      Colored jokers
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-medium text-gray-600 w-32">
                      Level 2:
                    </span>
                    <CardDisplay suit="joker" rank="Small" />
                    <CardDisplay suit="joker" rank="Small" />
                    <span className="text-xs text-gray-500 ml-2">
                      Small jokers
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-medium text-gray-600 w-32">
                      Level 3:
                    </span>
                    <CardDisplay suit="hearts" rank="7" isTrump />
                    <span className="text-xs text-gray-500 ml-2">
                      Trump rank + trump suit
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-medium text-gray-600 w-32">
                      Level 4:
                    </span>
                    <CardDisplay suit="spades" rank="7" isTrump />
                    <CardDisplay suit="diamonds" rank="7" isTrump />
                    <CardDisplay suit="clubs" rank="7" isTrump />
                    <span className="text-xs text-gray-500 ml-2">
                      Remaining trump rank cards (equal strength)
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-medium text-gray-600 w-32">
                      Level 5 (Lowest):
                    </span>
                    <CardDisplay suit="hearts" rank="A" isTrump />
                    <CardDisplay suit="hearts" rank="K" isTrump />
                    <span className="text-xs text-gray-500 ml-2">
                      Trump suit A down to 2 (skipping rank 7)
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4 italic">
                  Note: The trump rank changes each round based on the declarer
                  level. This example assumes the declarers are at level 7 and
                  won hearts during bidding.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Teams & Levels Section */}
        <section id="teams-levels" className="py-12 scroll-mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <Users className="w-8 h-8 text-red" />
              <h2 className="text-3xl font-bold text-black">Teams & Levels</h2>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <ul className="space-y-3 text-gray-700">
                <li>
                  Each partnership (or player, in Finding Friends) carries a{' '}
                  <strong className="text-black">level</strong> that starts at
                  rank 2 and climbs toward Ace.
                </li>
                <li>
                  The <strong className="text-black">declarer team</strong>{' '}
                  corresponds to the players defending their current level.
                  Their rank defines the trump rank for the next round.
                </li>
                <li>
                  A game ends when a team successfully defends at the Ace rank
                  (i.e., wins a round while at level Ace).
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Round Setup Section */}
        <section id="round-setup" className="py-12 scroll-mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="w-8 h-8 text-red" />
              <h2 className="text-3xl font-bold text-black">Round Setup</h2>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-8">
              {/* Dealing & Seat Order */}
              <div>
                <h3 className="text-xl font-bold text-black mb-3">
                  Dealing & Seat Order
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li>
                    Deal proceeds counter-clockwise with players drawing one
                    card at a time from stacked decks.
                  </li>
                  <li>
                    Every player draws 25 cards when using two decks, leaving 8
                    cards in the bottom pile. Three-deck games deal 39 cards
                    each and leave 6 bottom cards.
                  </li>
                  <li>
                    The player who ultimately wins the bid becomes the{' '}
                    <strong className="text-black">zhuangjia</strong> for the
                    round. In subsequent rounds:
                    <ul className="ml-6 mt-2 space-y-1">
                      <li>
                        • If declarers successfully defend, the leader passes to
                        their partner
                      </li>
                      <li>
                        • Otherwise the opposing team member to the
                        leader&apos;s right becomes the new leader
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>

              {/* Determining Trump Rank & Suit */}
              <div>
                <h3 className="text-xl font-bold text-black mb-3">
                  Determining Trump Rank & Suit
                </h3>
                <ol className="space-y-3 text-gray-700">
                  <li>
                    <strong className="text-black">Trump Rank</strong> equals
                    the current declarer level (e.g., declarers at level 7
                    produce trump rank 7 for the next round).
                  </li>
                  <li>
                    <strong className="text-black">Bidding Process:</strong>
                    <ul className="ml-6 mt-2 space-y-1">
                      <li>
                        • During the deal, any player may expose cards to bid.
                        Valid bids are composed of identical cards: trump rank
                        cards or jokers.
                      </li>
                      <li>
                        • Single-card bids establish a provisional suit; higher
                        bids must strictly beat prior bids first by quantity
                        (pairs beat singles, triples beat pairs, etc.) and then
                        by card strength (using no-trump ordering).
                      </li>
                      <li>
                        • Players may only reinforce their own bid by adding
                        identical cards of that same suit/rank; they cannot
                        pivot to a new suit without being overbid.
                      </li>
                      <li>
                        • Joker pairs declare a{' '}
                        <strong className="text-black">no-trump</strong> round.
                        A single joker is never a valid bid.
                      </li>
                    </ul>
                  </li>
                  <li>
                    <strong className="text-black">Winning Bid Outcome:</strong>{' '}
                    The suit of the final, highest bid becomes the trump suit.
                    If jokers win, the round is no-trump and only the trump rank
                    plus jokers form the trump suit.
                  </li>
                </ol>

                <RuleCallout type="tip">
                  Bid aggressively with jokers—a joker pair declares no-trump,
                  which can be powerful if you hold many trump-rank cards across
                  all suits.
                </RuleCallout>
              </div>

              {/* Leader Privileges */}
              <div>
                <h3 className="text-xl font-bold text-black mb-3">
                  Leader Privileges
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li>
                    The <strong className="text-black">zhuangjia</strong>{' '}
                    (leader) adds the bottom cards to their hand, then must
                    discard the same count face-down to rebuild the bottom pile.
                    Card count must remain constant (8 for two decks, 6 for
                    three decks, etc.).
                  </li>
                  <li>
                    Declarers may hide point cards in the bottom but risk
                    multipliers if the opponents capture the final trick.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom Cards Section */}
        <section id="bottom-cards" className="py-12 scroll-mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <Archive className="w-8 h-8 text-red" />
              <h2 className="text-3xl font-bold text-black">Bottom Cards</h2>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
              <p className="text-gray-700">
                If the opponents capture the final trick, flip the bottom pile.
                Any point cards inside are{' '}
                <strong className="text-black">multiplied</strong> based on the
                size of the last trick&apos;s format and awarded to the
                opponents as if appended to that trick.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="py-3 px-4 font-semibold text-black">
                        Trick Shape
                      </th>
                      <th className="py-3 px-4 font-semibold text-black">
                        Example
                      </th>
                      <th className="py-3 px-4 font-semibold text-black">
                        Multiplier
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                      <td className="py-3 px-4">Single</td>
                      <td className="py-3 px-4">A♣</td>
                      <td className="py-3 px-4">×2</td>
                    </tr>
                    <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                      <td className="py-3 px-4">
                        Pair / identical block of size 2
                      </td>
                      <td className="py-3 px-4">Q♣Q♣</td>
                      <td className="py-3 px-4">×4</td>
                    </tr>
                    <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                      <td className="py-3 px-4">
                        Triple / identical block of size 3
                      </td>
                      <td className="py-3 px-4">7♣7♣7♣</td>
                      <td className="py-3 px-4">×6</td>
                    </tr>
                    <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                      <td className="py-3 px-4">Tractor (length ≥2)</td>
                      <td className="py-3 px-4">JJQQ, 444555</td>
                      <td className="py-3 px-4">×(2 × length)</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Composite throw</td>
                      <td className="py-3 px-4" colSpan={2}>
                        Use the multiplier of the longest identical block within
                        the throw
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-sm text-gray-600">
                The multiplier can be generalized for longer throws: the longest
                matched block length{' '}
                <code className="bg-gray-100 px-1 py-0.5 rounded">L</code>{' '}
                produces{' '}
                <code className="bg-gray-100 px-1 py-0.5 rounded">2 × L</code>.
              </p>

              <RuleCallout type="tip">
                Hiding point cards in the bottom is risky but rewarding—if
                opponents never capture the final trick, those points vanish
                completely.
              </RuleCallout>
            </div>
          </div>
        </section>

        {/* Trick Play Section */}
        <section id="trick-play" className="py-12 scroll-mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <Zap className="w-8 h-8 text-red" />
              <h2 className="text-3xl font-bold text-black">Trick Play</h2>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-8">
              {/* Legal Trick Formats */}
              <div>
                <h3 className="text-xl font-bold text-black mb-3">
                  Legal Trick Formats
                </h3>
                <p className="text-gray-700 mb-4">
                  Every trick consumes the same number of cards from each
                  player. The opening play defines the &quot;format&quot;; only
                  plays matching the format&apos;s suit and hierarchy can win
                  the trick.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="py-3 px-4 font-semibold text-black">
                          Format
                        </th>
                        <th className="py-3 px-4 font-semibold text-black">
                          Requirements
                        </th>
                        <th className="py-3 px-4 font-semibold text-black">
                          Example
                        </th>
                        <th className="py-3 px-4 font-semibold text-black">
                          Notes
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                        <td className="py-3 px-4">Single</td>
                        <td className="py-3 px-4">Any one card</td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            <CardDisplay suit="spades" rank="K" />
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          Highest trump wins if any are played; otherwise
                          highest in lead suit wins
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                        <td className="py-3 px-4">Pair</td>
                        <td className="py-3 px-4">
                          Doubles, triples, quads, etc. of the same rank & suit
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1 flex-wrap">
                            <CardDisplay suit="clubs" rank="Q" />
                            <CardDisplay suit="clubs" rank="Q" />
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          Followers must play the same hierarchy in-suit if
                          possible
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                        <td className="py-3 px-4">Tractor</td>
                        <td className="py-3 px-4">
                          ≥2 consecutive identical blocks within one suit using
                          Shengji rank order
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1 flex-wrap">
                            <CardDisplay suit="hearts" rank="Q" />
                            <CardDisplay suit="hearts" rank="Q" />
                            <CardDisplay suit="hearts" rank="J" />
                            <CardDisplay suit="hearts" rank="J" />
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          Trump rank cards sit above Ace, creating adjacency
                          bridges
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4">Throw (Shuai)</td>
                        <td className="py-3 px-4">
                          Combination of highest-possible singles/pairs/tractors
                          in one suit
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1 flex-wrap items-center">
                            <CardDisplay suit="spades" rank="A" />
                            <span className="text-gray-400">+</span>
                            <CardDisplay suit="spades" rank="K" />
                            <CardDisplay suit="spades" rank="K" />
                            <span className="text-gray-400">+</span>
                            <CardDisplay suit="spades" rank="Q" />
                            <CardDisplay suit="spades" rank="J" />
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          Leader must ensure each component cannot be beaten
                          in-suit
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Following Rules */}
              <div>
                <h3 className="text-xl font-bold text-black mb-3">
                  Following Rules
                </h3>
                <div className="space-y-6">
                  <div>
                    <p className="text-gray-700 mb-4">
                      <strong className="text-black">
                        Satisfy suit and hierarchy:
                      </strong>{' '}
                      If you can match the exact hierarchy (e.g., a pair) in the
                      lead suit, you must. If you lack the full hierarchy but
                      have some cards in the suit, you must play as many as you
                      can, plus off-suit fillers.
                    </p>
                    <TrickExample
                      trumpSuit="hearts"
                      leadSuit="spades"
                      plays={[
                        {
                          playerName: 'Player A (Lead)',
                          card: { suit: 'spades', rank: 'K' },
                          note: 'Leads with K♠',
                          isWinner: true,
                        },
                        {
                          playerName: 'Player B',
                          card: { suit: 'spades', rank: 'Q' },
                          note: 'Follows with Q♠',
                        },
                        {
                          playerName: 'Player C',
                          card: { suit: 'hearts', rank: '7' },
                          note: 'Void in spades, trumps',
                        },
                        {
                          playerName: 'Player D',
                          card: { suit: 'spades', rank: 'J' },
                          note: 'Follows with J♠',
                        },
                      ]}
                      explanation="Player A leads K♠. Player B follows with Q♠. Player C is void in spades and trumps with 7♥ (the trump rank). Player D follows with J♠. Player A wins with the highest spade (K♠ beats Q♠ beats J♠), and C's trump does not win because the trump rank is lower than K♠."
                    />
                  </div>

                  <div>
                    <p className="text-gray-700 mb-4">
                      <strong className="text-black">Trumping:</strong> Only
                      players void in the lead suit may use trumps. To win,
                      their trumps must match the hierarchy structure (pair
                      beats pair, etc.).
                    </p>
                    <TrickExample
                      trumpSuit="hearts"
                      leadSuit="spades"
                      plays={[
                        {
                          playerName: 'Player A (Lead)',
                          card: { suit: 'spades', rank: '10' },
                        },
                        {
                          playerName: 'Player B',
                          card: { suit: 'clubs', rank: '9' },
                          note: 'Void in spades',
                        },
                        {
                          playerName: 'Player C',
                          card: { suit: 'hearts', rank: '7' },
                          note: 'Void in spades, trumps',
                          isWinner: true,
                        },
                        {
                          playerName: 'Player D',
                          card: { suit: 'spades', rank: '8' },
                          note: 'Follows suit',
                        },
                      ]}
                      explanation="Player A leads 10♠. Player B is void in spades and plays 9♣ (off-suit filler). Player C is also void and trumps with 7♥ (the trump rank). Player D follows suit with 8♠. Player C wins because trump beats the lead suit."
                    />
                  </div>

                  <div>
                    <p className="text-gray-700 mb-4">
                      <strong className="text-black">Throws:</strong> When
                      facing a throw, each component must be beaten
                      individually. A valid trump throw must match the entire
                      structure; mixing unmatched cards cannot defeat a throw.
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 p-4 rounded text-sm text-gray-700 mb-4">
                      <strong>Example scenario:</strong> Player A leads the
                      throw A♠ + K♠K♠ + Q♠J♠. Player C (void in spades) must
                      respond with a matching throw structure or play individual
                      cards; each component is beaten separately.
                    </div>
                  </div>

                  <div>
                    <p className="text-gray-700">
                      <strong className="text-black">
                        Equal strength resolution:
                      </strong>{' '}
                      Identical combinations compare by play order—earlier plays
                      win ties.
                    </p>
                  </div>
                </div>
              </div>

              {/* Tractor Connectivity */}
              <div>
                <h3 className="text-xl font-bold text-black mb-3">
                  Tractor Connectivity
                </h3>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li>
                    Use <strong className="text-black">Shengji order:</strong>{' '}
                    remove the trump rank from its natural spot and place it
                    above Ace. This causes gaps elsewhere, so sequences like
                    3-4-6 become consecutive when 5 is trump (because 5 moved to
                    the top, so 4 now touches 6).
                  </li>
                  <li>
                    Jokers form their own block at the very top and can
                    participate in tractors only when paired (e.g., red-red +
                    black-black).
                  </li>
                  <li>
                    Cross-suit tractors are never legal; all cards in the set
                    must share a suit (with trump rank cards counting as trump
                    suit).
                  </li>
                </ul>

                <div className="mt-6 p-6 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="text-lg font-semibold text-black mb-4">
                    Visual: How Trump Rank Creates New Adjacency
                  </h4>
                  <p className="text-sm text-gray-700 mb-4">
                    When <strong>5 is trump</strong>, the 5 moves above the Ace,
                    creating a gap between 4 and 6. This gap closes, making 4-6
                    consecutive for tractor purposes.
                  </p>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="text-sm font-medium text-gray-600 mb-2">
                        Natural Order (2-A):
                      </div>
                      <div className="flex gap-1 text-sm">
                        <span className="px-2 py-1 bg-white rounded border">
                          2
                        </span>
                        <span className="px-2 py-1 bg-white rounded border">
                          3
                        </span>
                        <span className="px-2 py-1 bg-white rounded border">
                          4
                        </span>
                        <span className="px-2 py-1 bg-yellow-200 rounded border font-bold">
                          5
                        </span>
                        <span className="px-2 py-1 bg-white rounded border">
                          6
                        </span>
                        <span className="px-2 py-1 bg-white rounded border">
                          7
                        </span>
                        <span className="px-2 py-1 bg-white rounded border">
                          ...
                        </span>
                        <span className="px-2 py-1 bg-white rounded border">
                          A
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-600 mb-2">
                        Shengji Order (5=Trump):
                      </div>
                      <div className="flex gap-1 text-sm">
                        <span className="px-2 py-1 bg-white rounded border">
                          2
                        </span>
                        <span className="px-2 py-1 bg-white rounded border">
                          3
                        </span>
                        <span className="px-2 py-1 bg-white rounded border">
                          4
                        </span>
                        <span className="px-2 py-1 bg-green-200 rounded border font-bold">
                          6
                        </span>
                        <span className="px-2 py-1 bg-green-200 rounded border font-bold">
                          7
                        </span>
                        <span className="px-2 py-1 bg-white rounded border">
                          ...
                        </span>
                        <span className="px-2 py-1 bg-white rounded border">
                          A
                        </span>
                        <span className="px-2 py-1 bg-yellow-200 rounded border font-bold">
                          →5
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-gray-700">
                    <strong>Valid Tractor:</strong> 4-6-7 is consecutive because
                    5 moved above A (shown as →5). The gap between 4 and 6
                    closed.
                  </div>
                  <RuleCallout type="tip">
                    When analyzing tractors, mentally remove the trump rank from
                    the natural order—this reveals which sequences are actually
                    connected.
                  </RuleCallout>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Scoring Section */}
        <section id="scoring" className="py-12 scroll-mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8 text-red" />
              <h2 className="text-3xl font-bold text-black">Scoring</h2>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-8">
              {/* Standard Two-Deck Table */}
              <div>
                <h3 className="text-xl font-bold text-black mb-3">
                  Standard Two-Deck Table (200 total points)
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="py-3 px-4 font-semibold text-black">
                          Opponents&apos; Points
                        </th>
                        <th className="py-3 px-4 font-semibold text-black">
                          Result
                        </th>
                        <th className="py-3 px-4 font-semibold text-black">
                          Level Change
                        </th>
                        <th className="py-3 px-4 font-semibold text-black">
                          Next Leader
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                        <td className="py-3 px-4">0</td>
                        <td className="py-3 px-4">
                          Declarers defend perfectly
                        </td>
                        <td className="py-3 px-4">+3 levels</td>
                        <td className="py-3 px-4">Partner of current leader</td>
                      </tr>
                      <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                        <td className="py-3 px-4">5–35</td>
                        <td className="py-3 px-4">Declarers defend solidly</td>
                        <td className="py-3 px-4">+2 levels</td>
                        <td className="py-3 px-4">Partner of current leader</td>
                      </tr>
                      <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                        <td className="py-3 px-4">40–75</td>
                        <td className="py-3 px-4">Declarers defend narrowly</td>
                        <td className="py-3 px-4">+1 level</td>
                        <td className="py-3 px-4">Partner of current leader</td>
                      </tr>
                      <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                        <td className="py-3 px-4">80–115</td>
                        <td className="py-3 px-4">
                          Opponents take control (no level change)
                        </td>
                        <td className="py-3 px-4">0</td>
                        <td className="py-3 px-4">
                          Player to leader&apos;s right
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                        <td className="py-3 px-4">120–155</td>
                        <td className="py-3 px-4">Opponents win cleanly</td>
                        <td className="py-3 px-4">Opponents +1 level</td>
                        <td className="py-3 px-4">
                          Player to leader&apos;s right
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50 transition-colors">
                        <td className="py-3 px-4">160–195</td>
                        <td className="py-3 px-4">Opponents rout</td>
                        <td className="py-3 px-4">Opponents +2 levels</td>
                        <td className="py-3 px-4">
                          Player to leader&apos;s right
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4">
                          ≥200 (includes bottom bonuses)
                        </td>
                        <td className="py-3 px-4">Opponents sweep</td>
                        <td className="py-3 px-4">
                          Opponents +3 levels (+1 extra per additional 40
                          points)
                        </td>
                        <td className="py-3 px-4">
                          Player to leader&apos;s right
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Finding Friends Section */}
        <section id="finding-friends" className="py-12 scroll-mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <UserPlus className="w-8 h-8 text-red" />
              <h2 className="text-3xl font-bold text-black">Finding Friends</h2>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
              <p className="text-gray-700">
                After inspecting the bottom, the{' '}
                <strong className="text-black">zhuangjia</strong> specifies
                which cards recruit teammates. A friend declaration ties a
                specific card to an ordinal appearance (e.g., &quot;second A♠
                played&quot;).
              </p>

              <div>
                <h3 className="text-lg font-bold text-black mb-2">
                  Constraints:
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li>
                    Friend cards must be ≤ Ace; trump-rank cards and jokers may
                    not be declared.
                  </li>
                  <li>
                    The leader may inadvertently declare their own cards; if
                    they play the declared card in the specified position, they
                    remain solo until another friend card is revealed.
                  </li>
                  <li>
                    Once a friend card is revealed in a trick, the player
                    immediately joins the defending team, and any points they
                    have already won transfer to that team.
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-bold text-black mb-2">Recap:</h3>
                <ol className="space-y-2 text-gray-700">
                  <li>
                    Use the same dealing and bidding structure as standard
                    Shengji.
                  </li>
                  <li>
                    Zhuangjia declares friend cards after taking the bottom.
                  </li>
                  <li>
                    Team composition can change mid-round as declared cards
                    appear; a player may remain solo if no friend cards emerge.
                  </li>
                  <li>
                    Scoring follows the same tables, but points travel with the
                    team that currently includes the player holding them.
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>
      </main>
      <BackToTop />
    </div>
  );
}
